import { JSX } from "react";
import { TFunction } from "i18n";
import { FacetType, FeatureFlagStatus, IFacet } from "shared-services";
import { IStat } from "shared-types";
import { FilterDrawerMediaFilterNames } from "../FilterDrawerMediaFilter";
import { IFilterTagProps } from "../../Atoms/FilterTag/FilterTag";
import { IFilterDrawerItem } from "../FilterControl/FilterDrawer/FilterDrawer";
import { IRangeSelectionValue } from "../../Molecules/RangeSelectionDropdown/RangeSelectionDropdown";
import { IOption } from "../../../generics/interfaces";
import { ICheckboxGroupValue } from "../CheckboxGroup/CheckboxGroup";
import { FilterMode } from "../../Molecules/FilterModeSection";

export enum FiltersFieldNames {
  Gender = "gender",
  Agents = "agents",
  Appearance = "appearance",
  ContractStatus = "contractStatus",
  PlayingAge = "playingAge",
  Locations = "locationId",
  MembershipStatus = "membershipStatus",
  Media = "media",
  HeritageEthnicityAndBackground = "heritageEthnicityAndBackground",
}

export enum HeritageEthnicityAndBackground {
  HeritageAndEthnicity = "heritageAndEthnicity",
  EthnicBackground = "ethnicBackground",
  HeritageAndEthnicityFilterMode = "heritageAndEthnicityFilterMode",
  HeritageAndEthnicityInfo = "heritageAndEthnicityInfo",
}

export type SetFilterValues<T> = (values: Partial<T>) => void;

export type Modifiers = { showFilterControl?: boolean } & Record<
  string,
  unknown
>;

export interface IGetFilterFieldConfig<T> {
  name: FiltersFieldNames;
  values: T;
  filterDrawerValues: T;
  setFilterValues: SetFilterValues<T>;
  setFilterDrawerValues: SetFilterValues<T>;
  facets: Record<FacetType, IFacet[]>;
  filterData: Record<FiltersFieldNames, unknown>;
  t: TFunction;
  facetNames?: FacetType[];
  stats?: IFilterStats;
  dataTestId?: string;
  modifiers?: Modifiers;
  flags?: FeatureFlagStatus;
}

export interface IFilterFieldConfig<T> {
  name: FiltersFieldNames;
  facetNames?: FacetType[];
  getFilterFiledProps: (props: IGetFilterFieldConfig<T>) =>
    | {
        tags: IFilterTagProps[];
        filterDrawerConfig: IFilterDrawerItem | null;
        filterControlComponent?: JSX.Element | null;
      }
    | undefined;
  defaultValue: unknown;
  dataTestId?: string;
  modifiers?: Modifiers;
  requestDataFunc?: () => Promise<unknown>;
}

export type FilterConfig<T> = IFilterFieldConfig<T>[];

export interface IUserFilters<T> {
  values: T;
  defaultValues: T;
  filterDrawerValues: T;
  setFilterValues: (nextValues: Partial<T>) => void;
  setFilterDrawerValues: (nextValues: Partial<T>) => void;
  facets: Record<FacetType, IFacet[]>;
  filterData: Record<FiltersFieldNames, unknown>;
  isLoading: boolean;
  error: unknown;
  config: FilterConfig<T>;
}

export interface IFilterStats {
  performersWithShowReelsCount?: number;
  performersWithAudioClipsCount?: number;
  heritageAndEthnicity?: Array<IStat<number>>;
  appearances?: Array<IStat<number>>;
}

export interface IFilterValues {
  [FiltersFieldNames.Gender]: ICheckboxGroupValue;
  [FiltersFieldNames.Appearance]: ICheckboxGroupValue;
  [FiltersFieldNames.Agents]: ICheckboxGroupValue;
  [FiltersFieldNames.ContractStatus]: ICheckboxGroupValue;
  [FiltersFieldNames.PlayingAge]: IRangeSelectionValue;
  [FiltersFieldNames.Locations]: IOption<string>[];
  [FiltersFieldNames.Media]: {
    [FilterDrawerMediaFilterNames.HasShowreels]: boolean;
    [FilterDrawerMediaFilterNames.HasAudioClips]: boolean;
  };
  [FiltersFieldNames.HeritageEthnicityAndBackground]: {
    [HeritageEthnicityAndBackground.HeritageAndEthnicityInfo]?: string;
    [HeritageEthnicityAndBackground.HeritageAndEthnicity]: ICheckboxGroupValue;
    [HeritageEthnicityAndBackground.EthnicBackground]: ICheckboxGroupValue;
    [HeritageEthnicityAndBackground.HeritageAndEthnicityFilterMode]: FilterMode;
  };
}
